import React, { useState, useEffect } from 'react';
import cl from './Events.module.css';
import AdminService from '../../API/AdminService';
import Spinner from '../../components/UI/Spinner/Spinner';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import logger from '../../utils/logger';
import {
    IconStudents, IconBook, IconPays, IconChart, IconRefresh,
    IconCalendar, IconTrophy, IconTrendUp, IconUser, IconArrowRight,
} from '../../components/Icons';


// Inline SVG-sparkline — компактный линейный график.
// Принимает массив значений, рисует поли-линию + мягкую заливку под ней.
// Подходит для KPI-карточек, где нужен микро-тренд без библиотеки графиков.
function Sparkline({ data, color = '#FBD46D', height = 40, width = 120 }) {
    if (!Array.isArray(data) || data.length < 2) {
        return <svg width={width} height={height} aria-hidden="true"/>;
    }
    const max = Math.max(...data, 1);
    const min = Math.min(...data);
    const range = max - min || 1;
    const step = width / (data.length - 1);
    const padding = 3;
    const usableHeight = height - padding * 2;

    const points = data.map((v, i) => {
        const x = i * step;
        const y = padding + usableHeight - ((v - min) / range) * usableHeight;
        return [x, y];
    });
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;
    const gradId = `sparkline-grad-${color.replace('#', '')}`;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
            <defs>
                <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.35"/>
                    <stop offset="100%" stopColor={color} stopOpacity="0"/>
                </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#${gradId})`}/>
            <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle
                cx={points[points.length - 1][0]}
                cy={points[points.length - 1][1]}
                r="3"
                fill={color}
                stroke="#fff"
                strokeWidth="2"
            />
        </svg>
    );
}


// Inline SVG-bar — компактный столбчатый микро-график.
// Используем в виджете «активность по дням» — тонкие столбики без подписей.
function MiniBars({ data, color = '#FBD46D', height = 36, width = 140, gap = 2 }) {
    if (!Array.isArray(data) || data.length === 0) {
        return <svg width={width} height={height} aria-hidden="true"/>;
    }
    const max = Math.max(...data, 1);
    const barWidth = (width - gap * (data.length - 1)) / data.length;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
            {data.map((v, i) => {
                const h = (v / max) * height;
                return (
                    <rect
                        key={i}
                        x={i * (barWidth + gap)}
                        y={height - h}
                        width={barWidth}
                        height={h}
                        rx={Math.min(barWidth / 2, 2)}
                        fill={color}
                        opacity={0.4 + (v / max) * 0.6}
                    />
                );
            })}
        </svg>
    );
}
// Регистрируем компоненты Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const MainJournal = () => {
    // Состояния для данных
    const [students, setStudents] = useState([]);
    const [courses, setCourses] = useState([]);
    const [events, setEvents] = useState([]);
    const [pays, setPays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Состояния для вычисленной статистики
    const [stats, setStats] = useState({
        totalStudents: 0,
        activeStudents: 0,
        totalCourses: 0,
        totalEvents: 0,
        totalRevenue: 0,
        averageCheck: 0,
        attendanceRate: 0
    });

    // Загрузка данных при монтировании
    useEffect(() => {
        loadDashboardData();
    }, []);

    // Получить массив месяцев с августа текущего учебного года
    const getMonthsFromAugust = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth(); // 0 = январь, 7 = август

        // Если сейчас до августа, то учебный год начался в прошлом году
        const startYear = currentMonth >= 7 ? currentYear : currentYear - 1;

        const months = [];
        const monthNames = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
        const monthNumbers = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

        // Добавляем месяцы от августа до декабря стартового года
        for (let i = 7; i < 12; i++) { // 7 = август, 11 = декабрь
            months.push({
                name: monthNames[i],
                number: monthNumbers[i],
                year: startYear
            });
        }

        // Добавляем месяцы от января до июля следующего года
        for (let i = 0; i < 7; i++) { // 0 = январь, 6 = июль
            months.push({
                name: monthNames[i],
                number: monthNumbers[i],
                year: startYear + 1
            });
        }

        return months;
    };

    // Загрузка всех данных для дашборда
    const loadDashboardData = async () => {
        setLoading(true);
        setError('');

        try {
            // Параллельная загрузка всех данных
            const [studentsRes, coursesRes, eventsRes, paysRes] = await Promise.all([
                AdminService.getAllStudents(),
                AdminService.getAllCourses(),
                AdminService.getAllEvents(),
                AdminService.getAllPays()
            ]);

            const studentsData = studentsRes.data || [];
            const coursesData = coursesRes.data || [];
            const eventsData = eventsRes.data || [];
            const paysData = paysRes.data || [];

            setStudents(studentsData);
            setCourses(coursesData);
            setEvents(eventsData);
            setPays(paysData);

            // Вычисляем статистику
            calculateStats(studentsData, coursesData, eventsData, paysData);

        } catch (error) {
            logger.error('Ошибка загрузки данных дашборда:', error);
            setError('Ошибка загрузки данных. Попробуйте обновить страницу.');
        } finally {
            setLoading(false);
        }
    };

    // Вычисление статистики
    const calculateStats = (studentsData, coursesData, eventsData, paysData) => {
        // Основная статистика
        const totalStudents = studentsData.length;
        const activeStudents = studentsData.filter(s => s.status === 'Активен').length;
        const totalCourses = coursesData.length;
        const totalEvents = eventsData.length;

        // Финансовая статистика
        const totalRevenue = paysData.reduce((sum, pay) => sum + (pay.price || 0), 0);
        const averageCheck = paysData.length > 0 ? Math.round(totalRevenue / paysData.length) : 0;

        // Статистика посещаемости
        const attendanceRate = calculateAttendanceRate(studentsData);

        setStats({
            totalStudents,
            activeStudents,
            totalCourses,
            totalEvents,
            totalRevenue,
            averageCheck,
            attendanceRate
        });
    };

    // ИСПРАВЛЕННАЯ функция расчета средней посещаемости
    const calculateAttendanceRate = (studentsData) => {
        let totalAttendanceRates = [];

        studentsData.forEach(student => {
            try {
                const journal = JSON.parse(student.journal || '{}');

                // Для каждого курса студента
                Object.keys(journal).forEach(courseId => {
                    const courseJournal = journal[courseId];
                    if (courseJournal.visit && Array.isArray(courseJournal.visit) && courseJournal.visit.length > 0) {
                        const visits = courseJournal.visit;

                        // Находим первое посещение (дату записи на курс)
                        const firstVisitDate = new Date(Math.min(...visits.map(d => new Date(d))));
                        const today = new Date();

                        // Считаем количество недель с первого посещения
                        const msInWeek = 7 * 24 * 60 * 60 * 1000;
                        const weeksFromStart = Math.floor((today - firstVisitDate) / msInWeek) + 1;

                        // Предполагаем 1 занятие в неделю
                        const possibleLessons = Math.max(weeksFromStart, visits.length);

                        // Вычисляем процент посещаемости для этого курса
                        const attendanceRate = Math.min((visits.length / possibleLessons) * 100, 100);
                        totalAttendanceRates.push(attendanceRate);
                    }
                });
            } catch (e) {
                logger.error('Ошибка парсинга журнала студента:', student.id, e);
            }
        });

        if (totalAttendanceRates.length === 0) return 0;

        // Возвращаем среднюю посещаемость
        const averageRate = totalAttendanceRates.reduce((sum, rate) => sum + rate, 0) / totalAttendanceRates.length;
        return Math.round(averageRate);
    };

    // ИСПРАВЛЕННАЯ функция: Посещаемость в количестве учеников по месяцам с августа
    const getAttendanceByStudentsCount = () => {
        const months = getMonthsFromAugust();
        const attendanceData = months.map(month => {
            const uniqueStudents = new Set(); // Используем Set для уникальности студентов

            students.forEach(student => {
                try {
                    const journal = JSON.parse(student.journal || '{}');

                    // Проверяем все курсы студента
                    Object.keys(journal).forEach(courseId => {
                        const courseJournal = journal[courseId];
                        if (courseJournal.visit && Array.isArray(courseJournal.visit)) {
                            // Проверяем есть ли посещения в этом месяце
                            const hasVisitsInMonth = courseJournal.visit.some(date => {
                                const visitDate = new Date(date);
                                return visitDate.getMonth() === (parseInt(month.number) - 1) &&
                                    visitDate.getFullYear() === month.year;
                            });

                            if (hasVisitsInMonth) {
                                uniqueStudents.add(student.id); // Добавляем ID студента
                            }
                        }
                    });
                } catch (e) {
                    logger.error('Ошибка парсинга журнала студента:', student.id, e);
                }
            });

            return uniqueStudents.size; // Возвращаем количество уникальных студентов
        });

        return {
            labels: months.map(m => m.name),
            datasets: [
                {
                    label: 'Количество посещавших учеников',
                    data: attendanceData,
                    borderColor: '#FFB946',
                    backgroundColor: 'rgba(255, 185, 70, 0.1)',
                    tension: 0.4,
                    fill: true,
                    borderWidth: 3
                }
            ]
        };
    };

    // НОВАЯ функция: Посещения по дням
    const getAttendanceByDays = () => {
        const attendanceByDate = {};

        students.forEach(student => {
            try {
                const journal = JSON.parse(student.journal || '{}');

                // Собираем все даты посещений из всех курсов
                Object.keys(journal).forEach(courseId => {
                    const courseJournal = journal[courseId];
                    if (courseJournal.visit && Array.isArray(courseJournal.visit)) {
                        courseJournal.visit.forEach(date => {
                            attendanceByDate[date] = (attendanceByDate[date] || 0) + 1;
                        });
                    }
                });
            } catch (e) {
                logger.error('Ошибка парсинга журнала студента:', student.id, e);
            }
        });

        // Сортируем по датам
        const sortedDates = Object.keys(attendanceByDate).sort((a, b) => new Date(a) - new Date(b));
        const attendanceData = sortedDates.map(date => attendanceByDate[date]);

        // Форматируем даты для отображения (день.месяц)
        const formattedDates = sortedDates.map(date => {
            const d = new Date(date);
            return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}`;
        });

        return {
            labels: formattedDates,
            datasets: [
                {
                    label: 'Количество посещений',
                    data: attendanceData,
                    borderColor: '#4299E1',
                    backgroundColor: 'rgba(66, 153, 225, 0.1)',
                    tension: 0.3,
                    fill: true,
                    borderWidth: 2,
                    pointBackgroundColor: '#4299E1',
                    pointBorderColor: '#FFFFFF',
                    pointBorderWidth: 2,
                    pointRadius: 4
                }
            ]
        };
    };

    // НОВАЯ функция: Общее количество посещений по месяцам
    const getTotalAttendanceByMonths = () => {
        const months = getMonthsFromAugust();
        const attendanceByMonth = {};

        // Инициализируем месяцы нулями
        months.forEach(month => {
            const key = `${month.year}-${month.number}`;
            attendanceByMonth[key] = 0;
        });

        // Собираем все посещения из всех студентов
        students.forEach(student => {
            try {
                const journal = JSON.parse(student.journal || '{}');

                // Проходим по всем курсам студента
                Object.keys(journal).forEach(courseId => {
                    const courseJournal = journal[courseId];
                    if (courseJournal.visit && Array.isArray(courseJournal.visit)) {
                        // Каждое посещение добавляем к соответствующему месяцу
                        courseJournal.visit.forEach(date => {
                            const visitDate = new Date(date);
                            const key = `${visitDate.getFullYear()}-${(visitDate.getMonth() + 1).toString().padStart(2, '0')}`;

                            if (attendanceByMonth[key] !== undefined) {
                                attendanceByMonth[key]++; // Увеличиваем счетчик посещений
                            }
                        });
                    }
                });
            } catch (e) {
                logger.error('Ошибка парсинга журнала студента:', student.id, e);
            }
        });

        // Формируем данные для графика
        const data = months.map(month => {
            const key = `${month.year}-${month.number}`;
            return attendanceByMonth[key];
        });

        return {
            labels: months.map(m => m.name),
            datasets: [
                {
                    label: 'Количество посещений',
                    data,
                    backgroundColor: '#FFB946',
                    borderColor: '#FF9F1C',
                    borderWidth: 2,
                    borderRadius: 4,
                    borderSkipped: false
                }
            ]
        };
    };

    // Данные для графика платежей с августа
    const getPaymentsChartData = () => {
        const months = getMonthsFromAugust();
        const monthlyPayments = {};

        // Инициализируем месяцы нулями
        months.forEach(month => {
            const key = `${month.year}-${month.number}`;
            monthlyPayments[key] = 0;
        });

        // Суммируем платежи по месяцам
        pays.forEach(pay => {
            if (pay.date) {
                const payDate = new Date(pay.date);
                const key = `${payDate.getFullYear()}-${(payDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyPayments[key] !== undefined) {
                    monthlyPayments[key] += pay.price || 0;
                }
            }
        });

        const data = months.map(month => {
            const key = `${month.year}-${month.number}`;
            return monthlyPayments[key];
        });

        return {
            labels: months.map(m => m.name),
            datasets: [
                {
                    label: 'Платежи (руб)',
                    data,
                    backgroundColor: '#48BB78',
                    borderColor: '#38A169',
                    borderWidth: 2
                }
            ]
        };
    };

    // Средний чек по месяцам
    const getAverageCheckByMonth = () => {
        const months = getMonthsFromAugust();
        const monthlyData = {};

        // Инициализируем данные
        months.forEach(month => {
            const key = `${month.year}-${month.number}`;
            monthlyData[key] = {
                month: month.name,
                totalAmount: 0,
                count: 0,
                averageCheck: 0
            };
        });

        // Группируем платежи по месяцам
        pays.forEach(pay => {
            if (pay.date && pay.price) {
                const payDate = new Date(pay.date);
                const key = `${payDate.getFullYear()}-${(payDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[key]) {
                    monthlyData[key].totalAmount += pay.price;
                    monthlyData[key].count++;
                    monthlyData[key].averageCheck = monthlyData[key].count > 0 ?
                        Math.round(monthlyData[key].totalAmount / monthlyData[key].count) : 0;
                }
            }
        });

        return Object.values(monthlyData);
    };

    // Количество оплат по месяцам
    const getPaymentCountByMonth = () => {
        const months = getMonthsFromAugust();
        const monthlyData = {};

        // Инициализируем данные
        months.forEach(month => {
            const key = `${month.year}-${month.number}`;
            monthlyData[key] = {
                month: month.name,
                count: 0,
                totalAmount: 0
            };
        });

        // Группируем платежи по месяцам
        pays.forEach(pay => {
            if (pay.date) {
                const payDate = new Date(pay.date);
                const key = `${payDate.getFullYear()}-${(payDate.getMonth() + 1).toString().padStart(2, '0')}`;
                if (monthlyData[key]) {
                    monthlyData[key].count++;
                    monthlyData[key].totalAmount += pay.price || 0;
                }
            }
        });

        return Object.values(monthlyData);
    };

    // Распределение учеников по количеству курсов
    const getStudentCourseDistribution = () => {
        const distribution = {
            '1 курс': 0,
            '2 курса': 0,
            '3+ курсов': 0
        };

        students.forEach(student => {
            try {
                const groups = JSON.parse(student.group || '[]');
                const courseCount = groups.length;

                if (courseCount === 1) {
                    distribution['1 курс']++;
                } else if (courseCount === 2) {
                    distribution['2 курса']++;
                } else if (courseCount >= 3) {
                    distribution['3+ курсов']++;
                }
            } catch (e) {
                logger.error('Ошибка парсинга групп студента:', student.id, e);
            }
        });

        const total = Object.values(distribution).reduce((sum, count) => sum + count, 0);

        return {
            distribution,
            total,
            percentages: Object.fromEntries(
                Object.entries(distribution).map(([key, count]) => [
                    key,
                    total > 0 ? Math.round((count / total) * 100) : 0
                ])
            )
        };
    };

    // Данные для круговой диаграммы статусов учеников
    const getStudentStatusChartData = () => {
        const statusCount = students.reduce((acc, student) => {
            const status = student.status || 'Не указан';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        return {
            labels: Object.keys(statusCount),
            datasets: [
                {
                    data: Object.values(statusCount),
                    backgroundColor: [
                        '#48BB78', // Активен - зеленый
                        '#FFB946', // Неактивен - желтый
                        '#4299E1', // Ожидание - синий
                        '#E53E3E'  // Другие - красный
                    ],
                    borderWidth: 3,
                    borderColor: '#ffffff'
                }
            ]
        };
    };

    // Получить топ курсов по популярности
    const getTopCourses = () => {
        const coursePopularity = {};

        // Считаем количество студентов на каждом курсе
        courses.forEach(course => {
            coursePopularity[course.id] = {
                name: course.name,
                students: 0,
                prepod: course.prepod
            };
        });

        // Подсчитываем студентов
        students.forEach(student => {
            try {
                const groups = JSON.parse(student.group || '[]');
                groups.forEach(courseId => {
                    if (coursePopularity[courseId]) {
                        coursePopularity[courseId].students++;
                    }
                });
            } catch (e) {
                logger.error('Ошибка парсинга групп студента:', student.id, e);
            }
        });

        // Сортируем и возвращаем топ-5
        return Object.values(coursePopularity)
            .sort((a, b) => b.students - a.students)
            .slice(0, 5);
    };

    // Получить статистику по преподавателям
    const getTeachersStats = () => {
        const teacherStats = {};

        courses.forEach(course => {
            const teacher = course.prepod || 'Не указан';
            if (!teacherStats[teacher]) {
                teacherStats[teacher] = {
                    courses: 0,
                    students: 0
                };
            }
            teacherStats[teacher].courses++;

            // Считаем студентов
            students.forEach(student => {
                try {
                    const groups = JSON.parse(student.group || '[]');
                    if (groups.includes(course.id)) {
                        teacherStats[teacher].students++;
                    }
                } catch (e) {
                    // Игнорируем ошибки парсинга
                }
            });
        });

        return Object.entries(teacherStats)
            .map(([name, stats]) => ({ name, ...stats }))
            .sort((a, b) => b.students - a.students)
            .slice(0, 3);
    };

    // Форматирование числа с разделителями
    const formatNumber = (num) => {
        return new Intl.NumberFormat('ru-RU').format(num);
    };

    // Конфигурация графиков в новом стиле
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 15,
                    font: {
                        size: 12,
                        weight: '600'
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(45, 55, 72, 0.9)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#FFB946',
                borderWidth: 1
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    color: '#718096',
                    font: {
                        size: 11,
                        weight: '500'
                    }
                }
            },
            y: {
                grid: {
                    borderDash: [2, 2],
                    color: 'rgba(113, 128, 150, 0.2)'
                },
                ticks: {
                    color: '#718096',
                    font: {
                        size: 11,
                        weight: '500'
                    }
                }
            }
        }
    };

    // Конфигурация для графика по дням (много точек)
    const dailyChartOptions = {
        ...chartOptions,
        scales: {
            ...chartOptions.scales,
            x: {
                ...chartOptions.scales.x,
                ticks: {
                    ...chartOptions.scales.x.ticks,
                    maxTicksLimit: 15, // Ограничиваем количество меток
                    callback: function (value, index, values) {
                        // Показываем каждую 3-ю дату
                        return index % 3 === 0 ? this.getLabelForValue(value) : '';
                    }
                }
            }
        }
    };

    if (loading) {
        return (
            <div className={cl.pagewrapper}>
                <Spinner size="large" text="Загружаем статистику" fullPage />
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.pagewrapper}>
                <div className={cl.wrapcontent1}>
                    <div className={cl.errorState}>
                        <h2>Ошибка загрузки</h2>
                        <p>{error}</p>
                        <button
                            className={cl.retryButton}
                            onClick={loadDashboardData}
                        >
                            Попробовать снова
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const averageCheckData = getAverageCheckByMonth();
    const paymentCountData = getPaymentCountByMonth();
    const courseDistribution = getStudentCourseDistribution();
    const attendanceByDays = getAttendanceByDays();

    // Подготовка данных для sparkline'ов на KPI-карточках.
    // Извлекаем из тех же месячных данных, что уходят в большие графики.
    const monthlyAttendanceArr = getTotalAttendanceByMonths().datasets[0].data || [];
    const monthlyPaymentsArr = getPaymentsChartData().datasets[0].data || [];
    // Активность учеников за последние 14 дней — для виджета.
    const last14DaysActivity = (attendanceByDays.datasets?.[0]?.data || []).slice(-14);

    // Подсчёт активных учеников / total для прогресс-бара.
    const activeRatio = stats.totalStudents > 0
        ? Math.round((stats.activeStudents / stats.totalStudents) * 100)
        : 0;

    const topCourses = getTopCourses();
    const topTeachers = getTeachersStats();
    const maxTopCourse = topCourses[0]?.students || 1;
    const maxTopTeacher = topTeachers[0]?.students || 1;

    return (
        <div className={cl.pagewrapper}>
            <div className={cl.wrapcontent1}>

                {/* ============================================================
                    HERO — тёмный градиент в стиле /login, узкий, без лишнего.
                    Слева заголовок, справа кнопка обновления + большое число
                    "выручка за год" с цветным акцентом.
                    ============================================================ */}
                <section className={cl.hero}>
                    <div className={`${cl.heroBlob} ${cl.heroBlob1}`}/>
                    <div className={`${cl.heroBlob} ${cl.heroBlob2}`}/>
                    <div className={`${cl.heroBlob} ${cl.heroBlob3}`}/>
                    <div className={cl.heroInner}>
                        <div className={cl.heroLeft}>
                            <div className={cl.heroEyebrow}>
                                <span className={cl.heroDot}/>
                                Учебный год активен
                            </div>
                            <h1 className={cl.heroTitle}>
                                Дашборд <span className={cl.heroAccent}>центра</span>
                            </h1>
                            <p className={cl.heroLead}>
                                {stats.activeStudents} активных учеников · {stats.totalCourses} курсов · посещаемость {stats.attendanceRate}%
                            </p>
                        </div>

                        <div className={cl.heroRight}>
                            <div className={cl.heroMetric}>
                                <div className={cl.heroMetricLabel}>Выручка с августа</div>
                                <div className={cl.heroMetricValue}>
                                    {formatNumber(stats.totalRevenue)} <span className={cl.heroMetricUnit}>₽</span>
                                </div>
                                <div className={cl.heroMetricFoot}>
                                    Средний чек {formatNumber(stats.averageCheck)} ₽
                                </div>
                            </div>
                            <button
                                className={cl.heroRefresh}
                                onClick={loadDashboardData}
                                title="Обновить данные"
                            >
                                <IconRefresh width={16} height={16}/>
                                <span>Обновить</span>
                            </button>
                        </div>
                    </div>
                </section>

                {/* ============================================================
                    BENTO — асимметричная сетка ключевых метрик.
                    Большая карточка слева (Учеников с детализацией и графиком),
                    три квадратные карточки справа (Курсы, Платежи, Посещаемость).
                    ============================================================ */}
                <section className={cl.bento}>
                    {/* Большой виджет — Ученики */}
                    <div className={`${cl.bentoCard} ${cl.bentoBig} ${cl.bentoAmber}`}>
                        <div className={cl.bentoHead}>
                            <div className={cl.bentoIcon}>
                                <IconStudents width={20} height={20}/>
                            </div>
                            <div className={cl.bentoLabel}>Ученики</div>
                        </div>
                        <div className={cl.bentoValueRow}>
                            <div className={cl.bentoValue}>{stats.totalStudents}</div>
                            <Sparkline data={monthlyAttendanceArr} color="#FBD46D" width={110} height={36}/>
                        </div>
                        <div className={cl.bentoMeta}>
                            <span className={cl.bentoMetaStrong}>{stats.activeStudents}</span>
                            <span className={cl.bentoMetaMuted}>активных</span>
                            <span className={cl.bentoMetaDot}/>
                            <span className={cl.bentoMetaStrong}>{stats.totalStudents - stats.activeStudents}</span>
                            <span className={cl.bentoMetaMuted}>в архиве</span>
                        </div>
                        <div className={cl.bentoProgress}>
                            <div className={cl.bentoProgressLabel}>
                                <span>Активность</span>
                                <span className={cl.bentoProgressValue}>{activeRatio}%</span>
                            </div>
                            <div className={cl.bentoProgressTrack}>
                                <div className={cl.bentoProgressFill} style={{ width: `${activeRatio}%` }}/>
                            </div>
                        </div>
                    </div>

                    {/* Курсы */}
                    <div className={`${cl.bentoCard} ${cl.bentoBlue}`}>
                        <div className={cl.bentoHead}>
                            <div className={cl.bentoIcon}>
                                <IconBook width={20} height={20}/>
                            </div>
                            <div className={cl.bentoLabel}>Курсы</div>
                        </div>
                        <div className={cl.bentoValue}>{stats.totalCourses}</div>
                        <div className={cl.bentoMetaSmall}>
                            + {stats.totalEvents} мероприятий
                        </div>
                    </div>

                    {/* Платежи (количество) */}
                    <div className={`${cl.bentoCard} ${cl.bentoGreen}`}>
                        <div className={cl.bentoHead}>
                            <div className={cl.bentoIcon}>
                                <IconPays width={20} height={20}/>
                            </div>
                            <div className={cl.bentoLabel}>Платежей</div>
                        </div>
                        <div className={cl.bentoValueRow}>
                            <div className={cl.bentoValue}>{pays.length}</div>
                            <Sparkline data={monthlyPaymentsArr} color="#10b981" width={70} height={28}/>
                        </div>
                        <div className={cl.bentoMetaSmall}>
                            за учебный год
                        </div>
                    </div>

                    {/* Посещаемость */}
                    <div className={`${cl.bentoCard} ${cl.bentoViolet}`}>
                        <div className={cl.bentoHead}>
                            <div className={cl.bentoIcon}>
                                <IconTrendUp width={20} height={20}/>
                            </div>
                            <div className={cl.bentoLabel}>Посещаемость</div>
                        </div>
                        <div className={cl.bentoValue}>
                            {stats.attendanceRate}<span className={cl.bentoValueUnit}>%</span>
                        </div>
                        <div className={cl.bentoMetaSmall}>
                            Активность 14 дней
                        </div>
                        <div className={cl.bentoMiniBars}>
                            <MiniBars data={last14DaysActivity} color="#a855f7" width={120} height={24}/>
                        </div>
                    </div>
                </section>

                {/* ============================================================
                    6 ВИДЖЕТОВ В СЕТКЕ 3×2.
                    Ряд 1: Платежи (график) · Активность (график) · Статусы (donut)
                    Ряд 2: Топ преподаватели · Популярные курсы · Распределение
                    ============================================================ */}
                <section className={cl.row2}>
                    {/* Платежи по месяцам */}
                    <article className={cl.cardMd}>
                        <header className={cl.cardHead}>
                            <div className={cl.cardHeadLeft}>
                                <span className={cl.cardHeadIcon}><IconPays width={14} height={14}/></span>
                                <h3 className={cl.cardTitle}>Платежи по месяцам</h3>
                            </div>
                        </header>
                        <div className={cl.chartHolder}>
                            <Bar
                                data={getPaymentsChartData()}
                                options={chartOptions}
                            />
                        </div>
                    </article>

                    {/* Активность по дням */}
                    <article className={cl.cardMd}>
                        <header className={cl.cardHead}>
                            <div className={cl.cardHeadLeft}>
                                <span className={cl.cardHeadIcon}><IconCalendar width={14} height={14}/></span>
                                <h3 className={cl.cardTitle}>Активность по дням</h3>
                            </div>
                        </header>
                        <div className={cl.chartHolder}>
                            <Bar
                                data={attendanceByDays}
                                options={dailyChartOptions}
                            />
                        </div>
                    </article>

                    {/* Статусы (donut) */}
                    <article className={cl.cardMd}>
                        <header className={cl.cardHead}>
                            <div className={cl.cardHeadLeft}>
                                <span className={cl.cardHeadIcon}><IconUser width={14} height={14}/></span>
                                <h3 className={cl.cardTitle}>Статусы учеников</h3>
                            </div>
                        </header>
                        <div className={cl.donutHolder}>
                            <Doughnut
                                data={getStudentStatusChartData()}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    cutout: '68%',
                                    plugins: {
                                        legend: {
                                            position: 'bottom',
                                            labels: {
                                                padding: 14,
                                                font: { size: 11, weight: '600' },
                                                color: '#475569',
                                                boxWidth: 8,
                                                boxHeight: 8,
                                                usePointStyle: true,
                                                pointStyle: 'circle',
                                            },
                                        },
                                    },
                                }}
                            />
                        </div>
                    </article>
                </section>

                <section className={cl.row3}>
                    {/* Топ преподаватели */}
                    <article className={cl.cardMd}>
                        <header className={cl.cardHead}>
                            <div className={cl.cardHeadLeft}>
                                <span className={cl.cardHeadIcon}><IconTrophy width={14} height={14}/></span>
                                <h3 className={cl.cardTitle}>Топ преподаватели</h3>
                            </div>
                        </header>
                        <ol className={cl.rankList}>
                            {topTeachers.slice(0, 5).map((teacher, index) => {
                                const pct = Math.round((teacher.students / maxTopTeacher) * 100);
                                return (
                                    <li key={index} className={cl.rankItem}>
                                        <div className={cl.rankNumber}>{index + 1}</div>
                                        <div className={cl.rankBody}>
                                            <div className={cl.rankName}>{teacher.name}</div>
                                            <div className={cl.rankMeta}>
                                                {teacher.courses} {teacher.courses === 1 ? 'курс' : 'курсов'}
                                            </div>
                                            <div className={cl.rankBar}>
                                                <div className={cl.rankBarFill} style={{ width: `${pct}%` }}/>
                                            </div>
                                        </div>
                                        <div className={cl.rankValue}>
                                            {teacher.students}
                                            <span className={cl.rankValueUnit}>уч.</span>
                                        </div>
                                    </li>
                                );
                            })}
                            {topTeachers.length === 0 && (
                                <li className={cl.rankEmpty}>Преподаватели не добавлены</li>
                            )}
                        </ol>
                    </article>

                    {/* Популярные курсы */}
                    <article className={cl.cardMd}>
                        <header className={cl.cardHead}>
                            <div className={cl.cardHeadLeft}>
                                <span className={cl.cardHeadIcon}><IconBook width={14} height={14}/></span>
                                <h3 className={cl.cardTitle}>Популярные курсы</h3>
                            </div>
                        </header>
                        <ol className={cl.rankList}>
                            {topCourses.slice(0, 5).map((course, index) => {
                                const pct = Math.round((course.students / maxTopCourse) * 100);
                                return (
                                    <li key={index} className={cl.rankItem}>
                                        <div className={cl.rankNumber}>{index + 1}</div>
                                        <div className={cl.rankBody}>
                                            <div className={cl.rankName}>{course.name}</div>
                                            {course.prepod && (
                                                <div className={cl.rankMeta}>{course.prepod}</div>
                                            )}
                                            <div className={cl.rankBar}>
                                                <div className={cl.rankBarFill} style={{ width: `${pct}%` }}/>
                                            </div>
                                        </div>
                                        <div className={cl.rankValue}>
                                            {course.students}
                                            <span className={cl.rankValueUnit}>уч.</span>
                                        </div>
                                    </li>
                                );
                            })}
                            {topCourses.length === 0 && (
                                <li className={cl.rankEmpty}>Нет данных</li>
                            )}
                        </ol>
                    </article>

                    {/* Распределение по количеству курсов */}
                    <article className={cl.cardMd}>
                        <header className={cl.cardHead}>
                            <div className={cl.cardHeadLeft}>
                                <span className={cl.cardHeadIcon}><IconChart width={14} height={14}/></span>
                                <h3 className={cl.cardTitle}>Ученики по курсам</h3>
                            </div>
                        </header>
                        <ul className={cl.distList}>
                            {Object.entries(courseDistribution.distribution).map(([category, count]) => (
                                <li key={category} className={cl.distItem}>
                                    <div className={cl.distHead}>
                                        <span className={cl.distLabel}>{category}</span>
                                        <span className={cl.distValue}>
                                            {count} <span className={cl.distPct}>· {courseDistribution.percentages[category]}%</span>
                                        </span>
                                    </div>
                                    <div className={cl.distTrack}>
                                        <div
                                            className={cl.distFill}
                                            style={{ width: `${courseDistribution.percentages[category]}%` }}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </article>
                </section>

            </div>
        </div>
    );
};

export default MainJournal;